// Resolve the existing published choice grammar by indices and typed refs.
// Descriptions/names are never used for membership. This validates selections
// only; it grants no item, title, skill advancement or world knowledge.
function requireValue(condition, message) {
  if (!condition) { throw new Error(message); }
}
function exactKeys(value, keys) {
  requireValue(value && typeof value === 'object' && !Array.isArray(value) &&
    Object.keys(value).sort().join(',') === keys.slice().sort().join(','), 'Invalid source choice selection fields');
}
function optionLeaves(option, selection, depth) {
  requireValue(depth <= 20 && option, 'Source choice nesting exceeds its bound');
  if (typeof option === 'string') {
    exactKeys(selection, ['optionIndex']);
    return [{ kind:'string', value:option }];
  }
  if (option.option_type === 'reference') {
    exactKeys(selection, ['optionIndex']);
    requireValue(option.item && typeof option.item.index === 'string' && option.item.index &&
      typeof option.item.url === 'string', 'Source choice reference is incomplete');
    return [{ kind:'reference', ref:option.item.index, url:option.item.url }];
  }
  if (option.option_type === 'choice') {
    exactKeys(selection, ['optionIndex','selection']);
    return resolveDefinitionChoice(option.choice, selection.selection, depth + 1);
  }
  if (option.option_type === 'multiple') {
    exactKeys(selection, ['optionIndex','selection']);
    requireValue(Array.isArray(option.items) && Array.isArray(selection.selection) &&
      option.items.length === selection.selection.length, 'Every component of a source option must be selected');
    return option.items.flatMap((item, index) => {
      const child = selection.selection[index];
      requireValue(child && child.optionIndex === index, 'Source option component order changed');
      return optionLeaves(item, child, depth + 1);
    });
  }
  throw new Error('Unsupported published source option type');
}

export function resolveDefinitionChoice(choice, selections, depth = 0) {
  requireValue(depth <= 20 && choice && Number.isInteger(choice.choose) && choice.choose >= 0 &&
    choice.choose <= 100 && choice.from && choice.from.option_set_type === 'options_array' &&
    Array.isArray(choice.from.options) && choice.from.options.length <= 10000, 'Unsupported or unbounded source choice definition');
  requireValue(Array.isArray(selections) && selections.length === choice.choose, 'Wrong number of source selections');
  const used = new Set();
  const leaves = selections.flatMap(selection => {
    requireValue(selection && Number.isInteger(selection.optionIndex) && selection.optionIndex >= 0 &&
      selection.optionIndex < choice.from.options.length && !used.has(selection.optionIndex), 'Duplicate or unavailable source option');
    used.add(selection.optionIndex);
    return optionLeaves(choice.from.options[selection.optionIndex], selection, depth + 1);
  });
  const keys = leaves.map(leaf => leaf.kind === 'reference' ? 'ref:' + leaf.ref : 'string:' + leaf.value);
  requireValue(new Set(keys).size === keys.length, 'A source choice cannot grant the same result twice');
  return leaves;
}

export function resolveClassProficiencies(input) {
  exactKeys(input, ['classDefinition','selections','source']);
  const cls = input.classDefinition;
  requireValue(cls && input.source && input.source.classRef === cls.index &&
    input.source.profile === 'dnd5e2014-srd51-class-sources.v1' &&
    Array.isArray(cls.proficiencies) && Array.isArray(cls.proficiency_choices), 'Class proficiency source missing');
  requireValue(Array.isArray(input.selections) && input.selections.length === cls.proficiency_choices.length,
    'Every class proficiency choice group must be supplied');
  const selected = cls.proficiency_choices.flatMap((group,index) => resolveDefinitionChoice(group,input.selections[index]));
  requireValue(selected.every(leaf => leaf.kind === 'reference' && leaf.url.startsWith('/api/2014/proficiencies/')),
    'Class selections must be typed proficiency references');
  const refs = selected.map(leaf => leaf.ref);
  requireValue(new Set(refs).size === refs.length, 'Class proficiency selections are duplicated');
  const fixed = cls.proficiencies.map(proficiency => proficiency.index);
  requireValue(new Set(fixed).size === fixed.length && refs.every(ref => !fixed.includes(ref)),
    'A class selection cannot duplicate its fixed proficiency');
  return { profile:'dnd5e2014.class-proficiencies.v1', stage:'validated_class_proficiency_selections',
    classRef:cls.index, fixedProficiencies:fixed, selectedProficiencies:refs,
    proficiencies:Array.from(new Set(fixed.concat(refs))).sort(), source:input.source };
}
